export type ParsingFn<TData> = (input: unknown) => TData;

/**
 * Fusionner cette interface ailleurs pour accepter d'autres formes de parseurs.
 *
 * @example
 * declare module "@mpa/toolbox/parsing/index" {
 *   interface ParserTypes<TData> {
 *     superstruct: Struct<TData>
 *   }
 * }
 */
export interface ParserTypes<TData> {
    fn: ParsingFn<TData>;
}

export type Parser<TData> = ParserTypes<TData>[keyof ParserTypes<TData>];

type ParserResolver = (parser: unknown) => ParsingFn<unknown> | undefined;

const parserResolvers: ParserResolver[] = [];

export function registerParser(resolver: ParserResolver): void {
    parserResolvers.push(resolver);
}

export function toParsingFn<TData>(parser: Parser<TData>): ParsingFn<TData> {
    if (typeof parser === "function") return parser;
    for (const resolve of parserResolvers) {
        const parsingFn = resolve(parser);
        if (parsingFn) return parsingFn as ParsingFn<TData>;
    }
    throw new Error("Unsupported parser");
}

export function parse<TData>(input: unknown, parser: Parser<TData>): TData {
    return toParsingFn(parser)(input);
}

export function mergeParsers<TData1, TData2>(
    p1: Parser<TData1> | null,
    p2: Parser<TData2> | null,
): Parser<TData1 & TData2> | null {
    if (p1 === null && p2 === null) return null;
    if (p1 === null) return p2 as Parser<TData1 & TData2>;
    if (p2 === null) return p1 as Parser<TData1 & TData2>;
    return (input) => {
        const data1 = parse(input, p1);
        const data2 = parse(input, p2);
        return { ...data1, ...data2 };
    };
}
